(() => {
  const signalR = require('@microsoft/signalr');

  // HM-11151 — the ACE Results Box shown above the form on a reimbursement claim's processing
  // page. Shared by the inpatient NPR page and (HM-11152) the outpatient page: it fetches its own
  // summary by claim number, so a page adopts it with one element and neither claim-detail DTO
  // has to carry an ACE payload (see ADR 0026). An ACE read that fails or is not permitted leaves
  // the box hidden and the form untouched.
  angular.module('app').component('commonComponentsAceResultsBoxComponent', {
    templateUrl: require('./aceResultsBox.component.html'),
    controller: AceResultsBoxController,
    controllerAs: 'vm',
    bindings: {
      claimNumber: '<',
      // Called with the parsed §11 analysis once ACE has a result, so the host page can apply its
      // own auto-fill. Never called for the not-submitted, pending, failed or timed-out states.
      onAnalysisReady: '<',
    },
  });

  AceResultsBoxController.$inject = ['$state', 'abp.services.app.aceClaims'];

  function AceResultsBoxController($state, aceClaimsSvc) {
    const vm = this;

    // The box's five states (ADR 0026). Four map onto the stored AceStatus; timedOut is derived
    // server-side from the submission age and is not a stored status.
    const NOT_SUBMITTED = 'notSubmitted';
    const PENDING = 'pending';
    const READY = 'ready';
    const FAILED = 'failed';
    const TIMED_OUT = 'timedOut';

    // Hms.Ace.AceStatus — Processing / Analysed / Failed / TimedOut (stored by the backstop
    // sweep since HM-11150, ADR 0030).
    const aceStatus = { processing: 0, analysed: 1, failed: 2, timedOut: 3 };

    // Hms.Ace.Dto.RefreshClaimStatusOutcome — Refreshed / NotFound / Error.
    const refreshOutcome = { refreshed: 0, notFound: 1, error: 2 };

    // Hms.Ace.Dto.RegisterClaimOutcome — Registered / NotFound / NotEligible / Error.
    const registerOutcome = { registered: 0, notFound: 1, notEligible: 2, error: 3 };

    // The box is a summary, but the default reasoning cut is generous (product review); anything
    // past it sits behind the row's View more toggle rather than being lost to the full page.
    const reasoningTruncateLength = 300;

    // One pill per non-ready state, so every scenario shares the ready state's header shape.
    const stateStyles = {
      notSubmitted: { labelKey: 'AceNotSubmitted', boxClass: 'ace-box-neutral', pillClass: 'ace-outcome-pill-neutral' },
      pending: { labelKey: 'AceAnalysisInProgress', boxClass: 'ace-box-neutral', pillClass: 'ace-outcome-pill-neutral' },
      failed: { labelKey: 'AceAnalysisFailed', boxClass: 'ace-box-fail', pillClass: 'ace-outcome-pill-fail' },
      timedOut: { labelKey: 'AceAnalysisTimedOut', boxClass: 'ace-box-neutral', pillClass: 'ace-outcome-pill-attention' },
    };

    // boxClass colours the outer panel, pillClass the outcome chip, cardClass a check's card.
    const outcomeStyles = {
      Pass: {
        labelKey: 'OutcomePass',
        boxClass: 'ace-box-pass',
        pillClass: 'ace-outcome-pill-pass',
        cardClass: '',
      },
      Fail: {
        labelKey: 'OutcomeFail',
        boxClass: 'ace-box-fail',
        pillClass: 'ace-outcome-pill-fail',
        cardClass: 'ace-check-card-fail',
      },
      RequestInfo: {
        labelKey: 'OutcomeRequestInfo',
        boxClass: 'ace-box-attention',
        pillClass: 'ace-outcome-pill-attention',
        cardClass: 'ace-check-card-attention',
      },
      Skip: {
        labelKey: 'OutcomeSkip',
        boxClass: 'ace-box-attention',
        pillClass: 'ace-outcome-pill-attention',
        cardClass: 'ace-check-card-attention',
      },
    };

    vm.loading = 0;
    vm.state = null;
    vm.summary = null;
    vm.outcome = null;
    vm.failedChecks = [];
    vm.passedCheckCount = 0;
    vm.totalCheckCount = 0;
    vm.omittedAttachments = [];
    vm.warnings = [];
    vm.unavailable = false;
    vm.canViewFullResults = abp.auth.isGranted('Claims.Ace.Host');

    vm.submitToAce = submitToAce;
    vm.checkForResults = checkForResults;
    vm.viewFullResults = viewFullResults;
    vm.outcomeStyle = outcomeStyle;
    vm.stateStyle = stateStyle;
    vm.needsReasoningToggle = needsReasoningToggle;
    vm.remarksSummary = remarksSummary;
    vm.hasRemarksDetail = hasRemarksDetail;
    vm.remarksExpanded = false;
    vm.checkTypeLabel = checkTypeLabel;
    vm.formatConfidence = formatConfidence;
    vm.truncateReasoning = truncateReasoning;
    vm.dash = dash;

    vm.$onInit = init;
    vm.$onDestroy = destroy;

    // One delivery guard per payload: a status-only push must not re-run the host page's
    // auto-fill over a processor's edits, but a genuinely new analysis (re-registration) must.
    let deliveredJson = null;

    // The details fetch is attempted at most once per page life, so a claim whose payload
    // cannot be pulled shows its status without the box looping fetch → reload → fetch.
    let detailsFetchAttempted = false;

    let hubConnection = null;

    function init() {
      if (!vm.claimNumber) {
        // Nothing to ask about — the box simply does not render.
        vm.unavailable = true;
        return;
      }

      // The summary read is host-gated, so an unpermitted processor (corporate self-manage
      // reaches this page) gets no box, no failed call and its error modal — and no hub
      // connection either. The gate lives here so every adopting page inherits it (ADR 0026).
      if (!vm.canViewFullResults) {
        vm.unavailable = true;
        return;
      }

      loadSummary();
      startStatusHubConnection();
    }

    function destroy() {
      if (hubConnection) {
        hubConnection.stop();
        hubConnection = null;
      }
    }

    // Live status push (HM-11150): the same AceClaimsHub broadcast the listing badges use. The
    // push and the backstop sweep move only the status — nothing server-side stores the §11
    // payload on Analysed — so on an Analysed push the box pulls the details itself, once.
    // Best effort: refused or unreachable, the box still works via load and Check for results.
    function startStatusHubConnection() {
      hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${abp.appPath}signalr/ace-claims`, {
          accessTokenFactory: () => App.getAccessToken(),
        })
        .withAutomaticReconnect()
        .build();

      hubConnection.on('aceClaimStatusUpdated', (message) => {
        if (!message || !message.claimNumber) return;
        if (message.claimNumber.toUpperCase() !== vm.claimNumber.toUpperCase()) return;

        if (message.aceStatus === aceStatus.analysed) {
          ensureDetailsThenReload();
        } else {
          loadSummary();
        }
      });

      hubConnection.start().catch(() => {
        // The box works without live updates.
      });
    }

    function ensureDetailsThenReload() {
      if (detailsFetchAttempted) {
        loadSummary();
        return;
      }

      fetchStoredDetails();
    }

    function loadSummary() {
      vm.loading += 1;
      aceClaimsSvc
        .getClaimAceSummary({ claimNumber: vm.claimNumber }, { abpHandleError: false })
        .success((data) => {
          applySummary(data);
        })
        .error(() => {
          // A failed read must never cost the processor the claim form — no ABP error modal.
          // Before anything has rendered the box withdraws; a failed *reload* keeps the box the
          // processor already has and says so quietly instead of blanking it for the page life.
          if (!vm.summary) {
            vm.unavailable = true;
            vm.state = null;
            return;
          }

          abp.notify.warn(App.localize('AceRefreshFailed'));
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function applySummary(data) {
      vm.summary = data || {};
      vm.state = resolveState(vm.summary);

      // Every derived section is rebuilt from this summary alone — a resubmit whose new payload
      // is unreadable must not keep rendering the previous analysis's checks and footnote.
      vm.outcome = null;
      vm.failedChecks = [];
      vm.passedCheckCount = 0;
      vm.totalCheckCount = 0;
      vm.omittedAttachments = [];
      vm.warnings = [];
      vm.remarksExpanded = false;

      if (vm.state === READY) {
        // Analysed rows routinely carry no stored payload — the sweep and the hub push move only
        // the status, and nothing server-side stores the §11 result. Pull it once here so the
        // normal path (ACE finished before the page was opened) still renders results and
        // auto-fills; if the pull fails, the ready state renders payload-less with its own
        // Check for results action as the recovery.
        if (!vm.summary.detailsJson && !detailsFetchAttempted) {
          fetchStoredDetails();
          return;
        }

        parseAnalysis(vm.summary.detailsJson);
      }
    }

    // Timed out is normally the stored status (backstop sweep, ADR 0030); isTimedOut also covers
    // a Processing row past the threshold that the hourly sweep has not stamped yet, so the flag
    // is still read before Processing.
    function resolveState(summary) {
      if (summary.aceStatus === null || summary.aceStatus === undefined) return NOT_SUBMITTED;
      if (summary.aceStatus === aceStatus.timedOut) return TIMED_OUT;
      if (summary.aceStatus === aceStatus.failed) return FAILED;
      if (summary.isTimedOut) return TIMED_OUT;
      if (summary.aceStatus === aceStatus.processing) return PENDING;
      if (summary.aceStatus === aceStatus.analysed) return READY;

      // An unrecognised status is reported rather than guessed at.
      return FAILED;
    }

    // The stored §11 payload is raw JSON from ACE, parsed here on the client exactly as the
    // full-analysis view does — nothing deserialises it server-side. A payload that cannot be
    // read costs the box its detail sections and any auto-fill, not the whole box.
    function parseAnalysis(rawJson) {
      const analysis = tryParse(rawJson);
      if (!analysis) return;

      buildOutcome(analysis.outcomeRecommendation);
      buildOmissionsAndWarnings(analysis.billingAnalysis);

      // Deliver each distinct payload to the host page exactly once — a reload prompted by a
      // status-only push re-renders the box but must not re-fill the form underneath the
      // processor. A changed payload is a new analysis and goes through.
      if (rawJson !== deliveredJson && angular.isFunction(vm.onAnalysisReady)) {
        deliveredJson = rawJson;
        vm.onAnalysisReady(analysis);
      }
    }

    function tryParse(rawJson) {
      if (!rawJson) return null;

      try {
        const analysis = JSON.parse(rawJson);
        return _.isObject(analysis) ? analysis : null;
      } catch (e) {
        return null;
      }
    }

    // Nullable §11 arrays can also carry null/garbage elements; only real objects render.
    function asObjects(list) {
      return _.filter(_.isArray(list) ? list : [], _.isObject);
    }

    function buildOutcome(outcomeRecommendation) {
      if (!outcomeRecommendation) return;

      const checks = asObjects(outcomeRecommendation.checks);

      vm.outcome = {
        overallOutcome: outcomeRecommendation.overallOutcome,
        overallDescription: outcomeRecommendation.overallDescription,
      };
      vm.totalCheckCount = checks.length;
      vm.failedChecks = _.filter(checks, (check) => check.outcome !== 'Pass');
      vm.passedCheckCount = vm.totalCheckCount - vm.failedChecks.length;
    }

    // Omissions and warnings are per-bill in the §11 payload; the box shows them as one list each,
    // because a processor cares that a document was skipped, not which bill skipped it.
    function buildOmissionsAndWarnings(billingAnalysis) {
      const bills = asObjects((billingAnalysis || {}).bills);

      vm.omittedAttachments = _.flatten(_.map(bills, (bill) => asObjects(bill.omittedAttachments)));
      vm.warnings = _.flatten(_.map(bills, (bill) => asObjects(bill.warning)));
    }

    function submitToAce() {
      vm.loading += 1;
      aceClaimsSvc
        .registerClaims({ claimNumbers: vm.claimNumber }, { abpHandleError: false })
        .success((data) => {
          // One number in, one verdict out — a 200 only means the batch ran. ACE refusing the
          // claim comes back per result, and a success toast over it would contradict the box
          // one reload later.
          const result = _.first((data || {}).results) || {};

          if (result.outcome !== registerOutcome.registered) {
            abp.notify.warn(result.remarks || App.localize('AceSubmitFailed'));
            loadSummary();
            return;
          }

          abp.notify.info(App.localize('AceSubmittedForAnalysis'));

          // A re-registration means a new analysis is coming: re-arm the one-shot details pull
          // and the delivery guard so the fresh result is fetched and handed to the page once.
          detailsFetchAttempted = false;
          deliveredJson = null;

          loadSummary();
        })
        .error(() => {
          abp.notify.warn(App.localize('AceSubmitFailed'));
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    // HM-11150 will poll for this on a schedule; until it exists the processor can ask. The chain
    // is exactly what that sweep will do for one claim: read ACE's status (API 7), and when it has
    // turned Analysed, pull and store the result (API 11). Both are the Phase 1.5 endpoints, so
    // this shows real ACE data rather than a placeholder.
    function checkForResults() {
      vm.loading += 1;
      aceClaimsSvc
        .refreshClaimStatus({ claimNumber: vm.claimNumber }, { abpHandleError: false })
        .success((data) => {
          if (data.outcome === refreshOutcome.error) {
            abp.notify.warn(App.localize('AceRefreshFailed'));
            return;
          }

          if (data.outcome === refreshOutcome.notFound) {
            // The claim has no ACE registration at all — telling the processor ACE is still
            // analysing it would be a lie. Reload so the box shows the not-submitted state.
            abp.notify.warn(App.localize('AceNotSubmitted'));
            loadSummary();
            return;
          }

          // API 11 rejects any status but Analysed, so the result is only pulled once ACE has one.
          if (data.status === aceStatus.analysed) {
            fetchStoredDetails();
            return;
          }

          if (data.status === aceStatus.failed) {
            // Saying "still analysing" over a failure would be a lie — same rule as notFound.
            abp.notify.warn(App.localize('AceRefreshFoundFailure'));
            loadSummary();
            return;
          }

          abp.notify.info(App.localize('AceStillProcessing'));
          loadSummary();
        })
        .error(() => {
          // ACE's failure detail can be a wall of upstream text (product review bug): keep the
          // full message in the server log and give the processor one short line here.
          abp.notify.warn(App.localize('AceRefreshFailed'));
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    // Stores ACE's result on the row; the box then re-reads its own summary so the state, the
    // derived timed-out flag and the payload all come from one consistent read.
    function fetchStoredDetails() {
      // One attempt per analysis: set here so the check-for-results path counts too, and the
      // push handler cannot loop fetch -> reload -> fetch on a claim whose payload will not pull.
      detailsFetchAttempted = true;

      vm.loading += 1;
      aceClaimsSvc
        .fetchFullClaimDetails({ claimNumber: vm.claimNumber }, { abpHandleError: false })
        .success(() => {
          loadSummary();
        })
        .error(() => {
          // The status moved even though the result could not be pulled — say so briefly and
          // show the new status rather than leaving the box claiming the claim is pending.
          abp.notify.warn(App.localize('AceRefreshFailed'));
          loadSummary();
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function viewFullResults() {
      // New tab, deliberately: this button sits above a form mid-entry, and $state.go would
      // discard everything typed. Same choice as the ACE listing's View results.
      window.open($state.href('aceClaimFullAnalysis', { claimNumber: vm.claimNumber }), '_blank');
    }

    function stateStyle() {
      return stateStyles[vm.state] || stateStyles.notSubmitted;
    }

    // A row gets a View more toggle only when the reasoning actually overflows the default cut.
    // ACE's failure remarks can carry a whole upstream stack trace. The box shows one readable
    // line — the first, capped — and the raw text stays behind the View more toggle for
    // diagnosis rather than being dumped on the processor.
    const remarksSummaryLength = 200;

    function remarksSummary() {
      const remarks = _.trim(vm.summary.failureRemarks || '');
      const firstLine = remarks.split(/\r?\n/)[0];

      return firstLine.length > remarksSummaryLength
        ? `${firstLine.substring(0, remarksSummaryLength)}…`
        : firstLine;
    }

    function hasRemarksDetail() {
      const remarks = _.trim(vm.summary.failureRemarks || '');
      const firstLine = remarks.split(/\r?\n/)[0];

      // Detail exists when the summary truncated the first line or there are further lines —
      // comparing against the summary text itself would be off by the appended ellipsis.
      return firstLine.length > remarksSummaryLength || remarks.length > firstLine.length;
    }

    function needsReasoningToggle(check) {
      return !!check.fullReasoning && check.fullReasoning.length > reasoningTruncateLength;
    }

    function outcomeStyle(outcome) {
      return (
        outcomeStyles[outcome] || {
          labelKey: null,
          boxClass: 'ace-box-neutral',
          pillClass: 'ace-outcome-pill-neutral',
          cardClass: '',
        }
      );
    }

    function checkTypeLabel(type) {
      if (!type) return dash(null);

      const key = `AceCheckType_${type}`;
      const localized = App.localize(key);
      return localized === key ? type : localized;
    }

    // Null-safe display: ACE's schema allows null on most leaf fields. 0 and false are real
    // values and pass through.
    function dash(value) {
      return value === null || value === undefined || value === '' ? '—' : value;
    }

    function formatConfidence(confidence) {
      if (confidence === null || confidence === undefined) return '—';
      return `${Math.round(confidence * 100)}%`;
    }

    function truncateReasoning(reasoning) {
      if (!reasoning) return '—';

      return reasoning.length > reasoningTruncateLength
        ? `${reasoning.substring(0, reasoningTruncateLength)}…`
        : reasoning;
    }
  }
})();
